import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { CatalogItem, CreateWizardPresaveLinkOptions } from '../../../redux/api/fymAPI.types';

/**
 * What the pre-save screen can do with one catalog song (design presave-create):
 *
 * - `ready`: an unreleased song with a release day of today or later and no pre-save page (a1).
 * - `needsDate`: an unreleased song with no day, or with a day in the past (a3).
 * - `hasPage`: the song already has a pre-save campaign (c3). The server ties one campaign to a row.
 * - `released`: a claimed song. It is out, so a SmartLink is the right page (c4).
 */
export type PresaveSongState = 'ready' | 'needsDate' | 'hasPage' | 'released';

/** `YYYY-MM-DD` in UTC, the day the release date is stored against. Undefined for a bad date. */
export function dayOfIso(isoDate: string | null): string | undefined {
  if (isoDate === null) {
    return undefined;
  }
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return parsed.toISOString().slice(0, 10);
}

/** Today in UTC (`YYYY-MM-DD`), the clock of the stored release dates. */
export function todayUtc(now: number): string {
  return new Date(now).toISOString().slice(0, 10);
}

export function presaveSongState(item: CatalogItem, today: string): PresaveSongState {
  if (item.presaveCampaignUuid !== null) {
    return 'hasPage';
  }
  if (item.kind !== CatalogItemKind.unreleased) {
    return 'released';
  }
  const day = dayOfIso(item.releaseDate);
  return day !== undefined && day >= today ? 'ready' : 'needsDate';
}

/**
 * The songs of the picker (design c1): the unreleased songs, the ones with a day first (soonest
 * first), then the ones with no day (newest added first). Released songs are left out.
 */
export function pickableSongs(items: CatalogItem[]): CatalogItem[] {
  const unreleased = items.filter((item) => item.kind === CatalogItemKind.unreleased);
  return [...unreleased].sort((a, b) => {
    const dayA = dayOfIso(a.releaseDate);
    const dayB = dayOfIso(b.releaseDate);
    if (dayA !== undefined && dayB !== undefined) {
      return dayA.localeCompare(dayB);
    }
    if (dayA !== undefined) {
      return -1;
    }
    if (dayB !== undefined) {
      return 1;
    }
    return b.claimedAt.localeCompare(a.claimedAt);
  });
}

/**
 * The body of `POST .../wizards/presaveLink` for a song: every fact comes from the row. An uploaded
 * cover goes as its file id (the page reuses the file); a store cover url goes as `imageURL`. Call
 * it only for a `ready` song: the day is then set.
 */
export function presaveCreateInput(
  item: CatalogItem,
  services: string[]
): Omit<CreateWizardPresaveLinkOptions, 'accountId'> {
  const imageId = item.artworkFile?.id;
  return {
    title: item.title,
    artistName: item.artistName,
    releaseDate: dayOfIso(item.releaseDate) ?? '',
    imageId,
    imageURL: imageId === undefined ? (item.artworkUrl ?? undefined) : undefined,
    catalogItemUuid: item.uuid,
    catalogTrackMaid: item.catalogTrackMaid ?? undefined,
    services,
  };
}

/**
 * Turns one service off or on. The last service that is on stays on: a pre-save block with no
 * service collects fans that nothing can be done for (the server refuses an empty list too).
 */
export function toggleService(selected: string[], service: string, order: string[]): string[] {
  if (selected.includes(service)) {
    return selected.length === 1 ? selected : selected.filter((one) => one !== service);
  }
  return order.filter((one) => one === service || selected.includes(one));
}
